/**
 * PurchaseOrdersView (PO-01) — ESC's PO history (read-only) plus Pegasus-native
 * PO creation (the write-machinery we cut over to later). Same house pattern:
 * searchable list → SlideOver detail; a "New PO" Modal with a vendor picker +
 * line items. ESC stays read-only (DEC-22).
 */
import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, Trash2, Check, Receipt, X, Pencil, Copy, ChevronDown, Wrench, Phone, BookOpen } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { SlideOver } from "@/components/ui/SlideOver";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { ComboBox } from "@/components/ui/ComboBox";
import { useEntryFormKeys, useListNav, scrollActiveIntoView } from "@/lib/keys";
import { type Item } from "@/features/items/items";
import { itemCostOf, usePricebookSearch } from "@/features/items/pricebookSearch";
import { PricebookBrowser, pbMoney } from "@/features/items/PricebookBrowser";
import { useVendorList } from "@/features/vendors/vendors";
import { AddressText } from "@/features/customer-360/AddressText";
import { PageHeader } from "@/components/ui/PageHeader";
import { SectionTitle } from "@/components/ui/PageHeader";
import { CopyCode, CopyListButton, copyText } from "@/components/Copyable";
import { InfoTip } from "@/components/InfoTip";
import { useCustomerJobs } from "@/features/jobs/jobs";
import {
  useEscPoList, useEscPoLines, useNativePos, useNativePoLines,
  useCreateNativePo, useUpdateNativePo, useUpdateNativePoStatus, useReceivePo, useBillPo, useCustomerLookup,
  useLocationEquipment, useAddUnit, useVendorCard, equipmentLabel,
  PO_TERMS, PO_SHIP_VIA, SEDGWICK_SHIP_TO, PO_ORDER_METHODS, PO_STATUS_LABEL,
  type EscPo, type NativePo, type NativePoLine, type NativePoLineInput, type CustomerLite, type PartEquipment, type PoOrderMethod, type PoStatus,
} from "./po";

const money = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** TICKET-PO-01: a part ticket's "Order (PO)" button lands here with the
 *  ticket's context so nobody re-types what the tech already wrote down.
 *  TICKET-EQUIP-01: `equip` is the unit the tech flagged it for (model / serial). */
interface PoPrefill { dispatch: string; custno: string; locno: string; part: string; desc: string; back?: string; equip?: PartEquipment | null; ticket?: string | null }
const fmtWhen = (iso: string | null) =>
  !iso ? "" : new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const who = (email: string | null) => (email ?? "").split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const installYear = (d: string | null | undefined) => {
  const y = d ? parseInt(d.slice(0, 4), 10) : NaN;
  return Number.isFinite(y) && y > 1950 ? y : null;
};
const fmtDate = (s: string | null) =>
  !s ? "" : new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const stripZeros = (po: string) => po.replace(/^0+/, "") || po;

/** "Ordered by" wears a NAME, not an email (Seth 2026-08-31). */
function useBuyerName(email: string | null | undefined) {
  return useQuery({
    queryKey: ["po_buyer_name", (email ?? "").toLowerCase()],
    enabled: !!email,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<string> => {
      const { data } = await supabase.from("mirror_employee")
        .select("firstname, lastname, empname").ilike("email", email!).limit(1).maybeSingle();
      const d = data as { firstname: string | null; lastname: string | null; empname: string | null } | null;
      const n = d ? [d.firstname, d.lastname].filter(Boolean).join(" ") || d.empname : null;
      return n || email!.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
  });
}

export function PurchaseOrdersView() {
  const [search, setSearch] = useState("");
  const { data: escPos, isLoading } = useEscPoList(search);
  const { data: nativePos } = useNativePos();
  const [openEsc, setOpenEsc] = useState<EscPo | null>(null);
  const [openNative, setOpenNative] = useState<NativePo | null>(null);
  // Deep links from Ask Pegasus ("pull up the purchase order in question"):
  // /purchase-orders?po=<native id> or ?escpo=<ESC PO number> opens the
  // detail directly. Handled once per param value.
  const [searchParams] = useSearchParams();
  const handledLink = useRef<string | null>(null);
  useEffect(() => {
    const nid = searchParams.get("po");
    const eno = searchParams.get("escpo");
    // WH-RESTOCK-03 (Seth: "a lot of clicks"): ?edit=<native id> from the
    // warehouse Restock list lands STRAIGHT in the full editor — no list row,
    // no preview, no sidebar-then-Edit.
    const eid = searchParams.get("edit");
    const key = `${nid ?? ""}|${eno ?? ""}|${eid ?? ""}`;
    if ((!nid && !eno && !eid) || handledLink.current === key) return;
    if (eid) {
      const hit = (nativePos ?? []).find((x) => x.id === eid);
      if (!hit) return; // wait for the list to load
      handledLink.current = key;
      setEditingNative(hit);
      return;
    }
    if (nid) {
      const hit = (nativePos ?? []).find((x) => x.id === nid);
      if (!hit) return; // wait for the list to load
      handledLink.current = key;
      setOpenNative(hit);
      return;
    }
    if (eno) {
      handledLink.current = key;
      void supabase.from("mirror_po").select("*").eq("po", eno).maybeSingle()
        .then(({ data }) => { if (data) setOpenEsc(data as EscPo); });
    }
  }, [searchParams, nativePos]);
  const [creating, setCreating] = useState(false);
  const [editingNative, setEditingNative] = useState<NativePo | null>(null);
  // PO-02: click a row → quick peek of its lines, right in the table.
  const [peekNat, setPeekNat] = useState<string | null>(null);
  const [peekEsc, setPeekEsc] = useState<string | null>(null);
  // ?new=1&dispatch=…&custno=… — opened from a part ticket, form pre-filled.
  const [prefill, setPrefill] = useState<PoPrefill | null>(null);
  const handledNew = useRef(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (handledNew.current || searchParams.get("new") !== "1") return;
    handledNew.current = true;
    let equip: PartEquipment | null = null;
    try {
      const raw = searchParams.get("equip");
      if (raw) equip = JSON.parse(raw) as PartEquipment;
    } catch { equip = null; }
    setPrefill({
      dispatch: searchParams.get("dispatch") ?? "",
      custno: searchParams.get("custno") ?? "",
      locno: searchParams.get("locno") ?? "",
      part: searchParams.get("part") ?? "",
      desc: searchParams.get("desc") ?? "",
      back: searchParams.get("back") ?? undefined,
      equip,
      ticket: searchParams.get("ticket") || null,
    });
    setCreating(true);
  }, [searchParams]);
  // Came from a Today part ticket → closing the form goes BACK TO TODAY with
  // the parts list, not stranded on the PO page (Seth 2026-08-31).
  const closeCreate = () => {
    const back = prefill?.back;
    setCreating(false);
    setPrefill(null);
    if (back === "today") navigate("/today");
  };

  const escList = escPos ?? [];
  const natList = nativePos ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6">
      <PageHeader
        icon={Receipt}
        title="Purchase Orders"
        description="Pegasus POs you create, plus the full ESC purchase history."
        actions={<Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New PO</Button>}
      />

      {natList.length > 0 && (
        <section className="mb-5">
          <SectionTitle
            title={<span className="inline-flex items-center gap-1.5">Pegasus POs<InfoTip label="About Pegasus POs">Purchase orders created in Pegasus, numbered 20001 and up.</InfoTip></span>}
            count={natList.length} className="mb-2" />
          <div className="overflow-hidden rounded-xl border border-hairline bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-slate-50/70 text-left text-[12px] font-semibold text-slate-500">
                  <th className="px-3 py-2">PO #</th>
                  <th className="px-3 py-2">Vendor</th>
                  <th className="px-3 py-2">For</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Date</th>
                  <th className="w-8 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {natList.map((p) => (
                  <Fragment key={p.id}>
                    <tr onClick={() => setPeekNat(peekNat === p.id ? null : p.id)}
                      title="Click for a quick peek at the lines"
                      className="cursor-pointer border-b border-hairline last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2 font-bold tabular-nums text-slate-900">
                        <CopyCode value={p.po_number != null ? String(p.po_number) : null} label="Copy PO number" variant="text" size="md" mono={false} className="font-bold">PO {p.po_number}</CopyCode>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{p.vendor_name ?? p.vendor_code ?? "—"}</td>
                      <td className="max-w-[200px] truncate px-3 py-2 text-slate-600">
                        {p.cust_name ?? (p.custno ? `#${p.custno}` : "")}{p.job_title ? `${p.cust_name || p.custno ? " · " : ""}${p.job_title}` : ""}
                        {!p.cust_name && !p.custno && !p.job_title && <span className="text-slate-300">stock</span>}
                      </td>
                      <td className="px-3 py-2"><StatusChip status={p.status} /></td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">{money(p.total)}</td>
                      <td className="px-3 py-2 text-right text-xs text-slate-400">{fmtDate(p.created_at.slice(0, 10))}</td>
                      <td className="px-2 py-2 text-center"><ChevronDown className={cn("inline h-3.5 w-3.5 text-slate-300 transition-transform", peekNat === p.id && "rotate-180")} /></td>
                    </tr>
                    {peekNat === p.id && <NativePeekRow po={p} onOpen={() => setOpenNative(p)} />}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="mb-3 flex items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search POs — number, vendor, part #, description, memo, customer, address…"
            className="w-full rounded-md border border-hairline py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        </div>
        <span className="text-xs text-slate-400">{escList.length} shown</span>
      </div>

      <SectionTitle
        title={<span className="inline-flex items-center gap-1.5">ESC History<InfoTip label="About ESC History">Every PO ever cut in ESC. Read-only here.</InfoTip></span>}
        className="mb-2" />
      <div className="overflow-x-auto rounded-xl border border-hairline bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/70 text-left text-[12px] font-semibold text-slate-500">
              <th className="px-3 py-2">PO #</th>
              <th className="px-3 py-2">Vendor</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Ship to</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>}
            {!isLoading && escList.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-400">No POs match.</td></tr>}
            {escList.map((p) => (
              <Fragment key={p.po}>
                <tr onClick={() => setPeekEsc(peekEsc === p.po ? null : p.po)}
                  title="Click for a quick peek at the lines"
                  className="cursor-pointer border-b border-hairline last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium tabular-nums text-slate-800">
                    <CopyCode value={stripZeros(p.po)} label="Copy PO number" variant="text" size="md" mono={false} className="font-medium" />
                  </td>
                  <td className="px-3 py-2 text-slate-600">{p.vendor_name ?? p.vendor_code ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">{fmtDate(p.date)}</td>
                  <td className="max-w-[220px] truncate px-3 py-2 text-slate-500">{p.ship_name}</td>
                  <td className="px-3 py-2 text-slate-500">{p.billed ? "Billed" : p.order_placed ? "Ordered" : "Open"}</td>
                </tr>
                {peekEsc === p.po && <EscPeekRow po={p} onOpen={() => setOpenEsc(p)} />}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {openEsc && <EscPoDetail po={openEsc} onClose={() => setOpenEsc(null)} />}
      {openNative && (
        <NativePoDetail po={openNative} onClose={() => setOpenNative(null)}
          onEdit={() => { setEditingNative(openNative); setOpenNative(null); }} />
      )}
      {creating && <PoFormModal prefill={prefill} onClose={closeCreate} />}
      {editingNative && <PoFormModal editPo={editingNative} onClose={() => setEditingNative(null)} />}
    </div>
  );
}

/** Quick-peek expansion rows (PO-02): the lines inline, then "open the full PO". */
function EscPeekRow({ po, onOpen }: { po: EscPo; onOpen: () => void }) {
  const { data: lines } = useEscPoLines(po.po);
  const total = (lines ?? []).reduce((s, l) => s + Number(l.amount ?? 0), 0);
  return (
    <tr className="border-b border-hairline bg-slate-50/60">
      <td colSpan={5} className="px-4 py-3">
        {po.memo && <p className="mb-2 text-xs text-slate-500"><span className="font-semibold text-slate-600">Memo: </span>{po.memo}</p>}
        <LineTable rows={(lines ?? []).map((l) => ({ part: l.part, description: l.description, qty: l.quan, cost: l.price, amount: l.amount, dispatch: l.dispatch_no }))} total={total} />
        <button type="button" onClick={onOpen} className="mt-2 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-800">
          Open the full PO
        </button>
      </td>
    </tr>
  );
}

function NativePeekRow({ po, onOpen }: { po: NativePo; onOpen: () => void }) {
  const { data: lines } = useNativePoLines(po.id);
  const total = (lines ?? []).reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);
  const buyerName = useBuyerName(po.buyer).data;
  return (
    <tr className="border-b border-hairline bg-slate-50/60">
      <td colSpan={7} className="px-4 py-3">
        <p className="mb-2 text-xs text-slate-500">
          {buyerName && <span className="mr-3"><span className="font-semibold text-slate-600">Ordered by: </span>{buyerName}</span>}
          {po.equipment && <span className="mr-3"><span className="font-semibold text-slate-600">For: </span>{equipmentLabel(po.equipment)}{po.equipment.serial ? ` · S/N ${po.equipment.serial}` : ""}</span>}
          {po.memo && <span><span className="font-semibold text-slate-600">Memo: </span>{po.memo}</span>}
        </p>
        <LineTable rows={(lines ?? []).map((l) => ({ part: l.part_number, description: l.description, qty: l.qty, cost: l.unit_cost, amount: Number(l.qty || 0) * Number(l.unit_cost || 0) }))} total={total} />
        <button type="button" onClick={onOpen} className="mt-2 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-800">
          Open the full PO
        </button>
      </td>
    </tr>
  );
}

function StatusChip({ status }: { status: PoStatus }) {
  const tone = status === "received" ? "bg-emerald-100 text-emerald-700"
    : status === "billed" ? "bg-slate-800 text-white"
    : status === "ordered" ? "bg-brand-100 text-brand-700"
    : status === "cancelled" ? "bg-rose-100 text-rose-700"
    : "bg-slate-100 text-slate-600";
  return <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", tone)}>{PO_STATUS_LABEL[status] ?? status}</span>;
}

/** TICKET-EQUIP-01: the unit a part is for — model + serial with copy, the
 *  two things the parts manager needs to order the right hard-start kit. */
function EquipmentCard({ e, compact, onClear }: { e: PartEquipment; compact?: boolean; onClear?: () => void }) {
  const yr = installYear(e.install_date);
  const age = yr ? new Date().getFullYear() - yr : null;
  return (
    <div className={cn("rounded-lg border border-hairline bg-white", compact ? "px-2.5 py-1.5" : "px-3 py-2")}>
      <div className="flex items-start gap-2">
        <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />
        <div className="min-w-0 flex-1 text-xs text-slate-700">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-semibold text-slate-900">{equipmentLabel(e)}</span>
            {e.eq_type && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{e.eq_type}</span>}
            {yr && <span className="text-[11px] text-slate-400">installed {yr}{age != null ? ` · ${age} yr${age === 1 ? "" : "s"}` : ""}</span>}
            {e.equip_loc && <span className="text-[11px] text-slate-400">· {e.equip_loc}</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
            <span className="text-slate-500">Model {e.model ? <CopyCode value={e.model} label="Copy model number" /> : <span className="text-slate-300">—</span>}</span>
            <span className="text-slate-500">S/N {e.serial ? <CopyCode value={e.serial} label="Copy serial number" /> : <span className="text-slate-300">not on file</span>}</span>
          </div>
        </div>
        {onClear && (
          <button type="button" onClick={onClear} title="Not this unit" className="shrink-0 rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
    </div>
  );
}

function EscPoDetail({ po, onClose }: { po: EscPo; onClose: () => void }) {
  const { data: lines } = useEscPoLines(po.po);
  const total = (lines ?? []).reduce((s, l) => s + Number(l.amount ?? 0), 0);
  const meta: [string, string | null][] = [
    ["Date", fmtDate(po.date)],
    ["Requested", fmtDate(po.date_req)],
    ["Terms", po.terms],
    ["Ship via", po.ship_via],
    ["Buyer", po.buyer],
    ["Status", po.billed ? "Billed" : po.order_placed ? "Ordered" : "Open"],
  ];
  return (
    <SlideOver open onClose={onClose} size="lg" title={`PO ${stripZeros(po.po)}`}
      subtitle={po.vendor_name ?? po.vendor_code ?? ""}>
      <div className="space-y-4">
        <dl className="grid grid-cols-3 gap-x-4 gap-y-2.5">
          {meta.filter(([, v]) => v).map(([k, v]) => (
            <div key={k}>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{k}</dt>
              <dd className="text-sm text-slate-700">{v}</dd>
            </div>
          ))}
        </dl>
        {(po.custno || po.dispatch) && (
          <div className="rounded-lg border border-brand-100 bg-brand-50/40 px-3 py-2 text-xs text-slate-600">
            <span className="font-medium text-brand-700">For job: </span>
            {po.custno && (
              <Link to={`/customers/${po.custno}${po.locno ? `?loc=${po.locno}` : ""}`} className="text-brand-700 hover:underline">customer {po.custno}</Link>
            )}
            {po.dispatch && (
              <Link to={`/dispatch/${po.dispatch}`} className="ml-2 text-brand-700 hover:underline">dispatch {po.dispatch}</Link>
            )}
          </div>
        )}
        {po.ship_name && <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">Ship to: </span>{po.ship_name}</p>}
        {po.memo && (
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Memo / RMA</span>
            <div className="mt-0.5"><AddressText text={po.memo} selfCustno={po.custno} /></div>
          </div>
        )}
        <LineTable rows={(lines ?? []).map((l) => ({ part: l.part, description: l.description, qty: l.quan, cost: l.price, amount: l.amount, dispatch: l.dispatch_no }))} total={total} />
        <p className="text-[11px] text-slate-400">From ESC — read-only.</p>
      </div>
    </SlideOver>
  );
}

function NativePoDetail({ po, onClose, onEdit }: { po: NativePo; onClose: () => void; onEdit: () => void }) {
  const { data: lines } = useNativePoLines(po.id);
  const setStatus = useUpdateNativePoStatus();
  const receive = useReceivePo();
  const bill = useBillPo();
  const vendor = useVendorCard(po.vendor_code).data;
  const total = (lines ?? []).reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);
  const busy = setStatus.isPending || receive.isPending || bill.isPending;
  // PO-03: one panel at a time — Place (draft → ordered), Receive (per line), Bill (vendor invoice).
  const [panel, setPanel] = useState<"place" | "receive" | "bill" | null>(null);
  const [method, setMethod] = useState<PoOrderMethod | "">(po.order_method ?? "");
  const [vendorRef, setVendorRef] = useState(po.vendor_order_ref ?? "");
  const [expected, setExpected] = useState(po.expected_date ?? "");
  const [recv, setRecv] = useState<Record<string, number>>({});
  const [recvNote, setRecvNote] = useState("");
  const [invNo, setInvNo] = useState(po.vendor_invoice_no ?? "");
  const [invDate, setInvDate] = useState(po.vendor_invoice_date ?? new Date().toISOString().slice(0, 10));
  const [invAmt, setInvAmt] = useState<string>(po.vendor_invoice_amount != null ? String(po.vendor_invoice_amount) : "");
  const remaining = (l: NativePoLine) => Math.max(Number(l.qty || 0) - Number(l.qty_received || 0), 0);
  const openReceive = () => {
    const init: Record<string, number> = {};
    for (const l of lines ?? []) init[l.id] = remaining(l);
    setRecv(init); setPanel("receive");
  };
  const doReceive = () => {
    const payload = (lines ?? []).map((l) => ({ id: l.id, qty: Math.min(Math.max(Number(recv[l.id] ?? 0), 0), remaining(l)) }));
    receive.mutate({ poId: po.id, lines: payload, note: recvNote.trim() || null }, { onSuccess: onClose });
  };
  const anyToReceive = Object.values(recv).some((n) => Number(n) > 0);
  const footer = (
    <div className="flex flex-wrap items-center gap-2">
      {po.status === "draft" && (
        <Button variant="secondary" size="sm" disabled={busy} onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
      )}
      {(po.status === "draft" || po.status === "ordered") && (
        <Button variant="ghost" size="sm" className="text-rose-600" disabled={busy}
          onClick={() => setStatus.mutate({ id: po.id, status: "cancelled" }, { onSuccess: onClose })}>Cancel PO</Button>
      )}
      {po.status === "draft" && (
        <Button size="sm" className="ml-auto" disabled={busy} onClick={() => setPanel(panel === "place" ? null : "place")}>
          <Check className="h-3.5 w-3.5" /> Place with vendor
        </Button>
      )}
      {po.status === "ordered" && (
        <Button size="sm" className="ml-auto" disabled={busy} onClick={() => (panel === "receive" ? setPanel(null) : openReceive())}>
          <Check className="h-3.5 w-3.5" /> Receive
        </Button>
      )}
      {po.status === "received" && (
        <Button size="sm" className="ml-auto" disabled={busy} onClick={() => setPanel(panel === "bill" ? null : "bill")}>
          <Receipt className="h-3.5 w-3.5" /> Enter vendor invoice
        </Button>
      )}
    </div>
  );
  const buyerName = useBuyerName(po.buyer).data;
  const orderedByName = useBuyerName(po.ordered_by).data;
  const methodLabel = PO_ORDER_METHODS.find((m) => m.value === po.order_method)?.label ?? null;
  const meta: [string, string | null][] = [
    ["Requested", po.date_requested ? fmtDate(po.date_requested) : null],
    ["Expected", po.expected_date ? fmtDate(po.expected_date) : null],
    ["Terms", po.terms],
    ["Ship via", po.ship_via],
    ["Created by", buyerName ?? (who(po.buyer) || null)],
    ["Ordered", po.ordered_at ? `${fmtWhen(po.ordered_at)}${orderedByName || po.ordered_by ? ` · ${orderedByName ?? who(po.ordered_by)}` : ""}${methodLabel ? ` · ${methodLabel.toLowerCase()}` : ""}` : (po.status === "draft" ? "not placed yet" : null)],
    ["Vendor order #", po.vendor_order_ref],
    ["Received", po.received_at ? `${fmtWhen(po.received_at)}${po.received_by ? ` · ${who(po.received_by)}` : ""}` : null],
    ["Vendor invoice", po.vendor_invoice_no ? `${po.vendor_invoice_no}${po.vendor_invoice_date ? ` · ${fmtDate(po.vendor_invoice_date)}` : ""}${po.vendor_invoice_amount != null ? ` · ${money(po.vendor_invoice_amount)}` : ""}` : null],
    ["Cancelled", po.cancelled_at ? `${fmtWhen(po.cancelled_at)}${po.cancelled_by ? ` · ${who(po.cancelled_by)}` : ""}` : null],
  ];
  const partial = (lines ?? []).some((l) => Number(l.qty_received || 0) > 0) && po.status === "ordered";
  return (
    <SlideOver open onClose={onClose} size="lg" footer={footer}
      title={`PO ${po.po_number ?? ""}`} subtitle={po.vendor_name ?? po.vendor_code ?? ""}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip status={po.status} />
          {partial && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">Partly received</span>}
          {vendor && (vendor.phone || vendor.account) && (
            <span className="ml-auto inline-flex items-center gap-2 text-[11.5px] text-slate-500">
              {vendor.phone && <a href={`tel:${vendor.phone}`} className="inline-flex items-center gap-1 text-brand-700 hover:underline"><Phone className="h-3 w-3" />{vendor.phone}</a>}
              {vendor.account && <span>Acct <CopyCode value={vendor.account} label="Copy our account number" /></span>}
              <Link to={`/vendors?vendor=${encodeURIComponent(vendor.vendor_code)}`} className="text-slate-400 hover:text-brand-700 hover:underline">vendor record</Link>
            </span>
          )}
        </div>
        <dl className="grid grid-cols-3 gap-x-4 gap-y-2.5">
          {meta.filter(([, v]) => v).map(([k, v]) => (
            <div key={k}>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{k}</dt>
              <dd className="text-sm text-slate-700">{v}</dd>
            </div>
          ))}
        </dl>
        {(po.custno || po.job_id || po.dispatch_no) && (
          <div className="rounded-lg border border-brand-100 bg-brand-50/40 px-3 py-2 text-xs text-slate-600">
            <span className="font-medium text-brand-700">For: </span>
            {po.custno && <Link to={`/customers/${po.custno}${po.locno ? `?loc=${po.locno}` : ""}`} className="text-brand-700 hover:underline">{po.cust_name ?? `customer ${po.custno}`}</Link>}
            {po.dispatch_no && /^\d{4,8}$/.test(po.dispatch_no.trim()) && <Link to={`/dispatch/${po.dispatch_no}`} className="ml-1.5 text-brand-700 hover:underline">dispatch {po.dispatch_no}</Link>}
            {po.job_id && <span className="ml-1.5">· job <span className="font-medium text-slate-700">{po.job_title ?? "Install"}</span></span>}
            <InfoTip label="Where the parts go" className="ml-1.5">{po.job_id ? "The parts show on this job for job costing." : "The parts attach to the address."}</InfoTip>
          </div>
        )}
        <p className="text-xs text-slate-500">
          <span className="font-medium text-slate-600">Ship to: </span>
          {(po.ship_to_addr ?? SEDGWICK_SHIP_TO).split("\n").join(" · ")}
          {po.ship_to_addr && po.ship_to_addr.trim() !== SEDGWICK_SHIP_TO && <span className="ml-1 rounded bg-amber-50 px-1 py-px text-[10px] font-semibold text-amber-700">not the office</span>}
        </p>
        {po.equipment && <EquipmentCard e={po.equipment} compact />}
        {po.memo && <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">{po.memo}</p>}
        {po.received_note && <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">Receiving note: </span>{po.received_note}</p>}

        {panel === "place" && (
          <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3">
            <div className="text-xs font-semibold text-slate-800">Placed with the vendor</div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <PoField label="How">
                <select value={method} onChange={(e) => setMethod(e.target.value as PoOrderMethod | "")} className="poinp w-full">
                  <option value="">—</option>
                  {PO_ORDER_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </PoField>
              <PoField label="Vendor order # (optional)"><input value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} className="poinp w-full" placeholder="their confirmation" /></PoField>
              <PoField label="Expected"><input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className="poinp w-full" /></PoField>
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" disabled={busy} onClick={() => setStatus.mutate({ id: po.id, status: "ordered", order_method: method || null, vendor_order_ref: vendorRef, expected_date: expected || null }, { onSuccess: onClose })}>
                <Check className="h-3.5 w-3.5" /> {setStatus.isPending ? "Saving…" : "Mark ordered"}
              </Button>
            </div>
          </div>
        )}

        {panel === "receive" && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-800">
              What came in?
              <InfoTip label="How receiving works">Full quantities are filled in, so lower any line that came in short. Received parts go into stock, and the waiting ticket shows Arrived.</InfoTip>
            </div>
            <div className="mt-2 space-y-1.5">
              {(lines ?? []).map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-xs">
                  <span className="min-w-0 flex-1 truncate text-slate-700"><span className="font-medium">{l.part_number ?? "—"}</span>{l.description ? <span className="text-slate-500"> — {l.description}</span> : null}</span>
                  <span className="shrink-0 text-[11px] text-slate-400">{Number(l.qty_received || 0)} of {l.qty} so far</span>
                  <input type="number" min={0} max={remaining(l)} value={recv[l.id] ?? 0}
                    onChange={(e) => setRecv((r) => ({ ...r, [l.id]: Number(e.target.value) }))}
                    className="poinp w-16 text-right" disabled={remaining(l) === 0} />
                </div>
              ))}
            </div>
            <input value={recvNote} onChange={(e) => setRecvNote(e.target.value)} className="poinp mt-2 w-full" placeholder="Note (optional) — e.g. one box damaged, vendor sending a replacement" />
            <div className="mt-2 flex justify-end">
              <Button size="sm" disabled={busy || !anyToReceive} onClick={doReceive}>
                <Check className="h-3.5 w-3.5" /> {receive.isPending ? "Receiving…" : "Receive"}
              </Button>
            </div>
          </div>
        )}

        {panel === "bill" && (
          <div className="rounded-lg border border-slate-300 bg-slate-50 p-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-800">
              Vendor invoice
              <InfoTip label="What this step does">ESC&apos;s Bill step: it records the vendor&apos;s invoice against this PO for job costing. It doesn&apos;t pay the bill — the bookkeeper still enters it in QuickBooks.</InfoTip>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <PoField label="Invoice #"><input value={invNo} onChange={(e) => setInvNo(e.target.value)} className="poinp w-full" /></PoField>
              <PoField label="Invoice date"><input type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)} className="poinp w-full" /></PoField>
              <PoField label="Amount"><input type="number" step="0.01" value={invAmt} onChange={(e) => setInvAmt(e.target.value)} className="poinp w-full text-right" placeholder={total ? total.toFixed(2) : ""} /></PoField>
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" disabled={busy || !invNo.trim()} onClick={() => bill.mutate({ id: po.id, invoice_no: invNo, invoice_date: invDate || null, amount: invAmt.trim() ? Number(invAmt) : (total || null) }, { onSuccess: onClose })}>
                <Check className="h-3.5 w-3.5" /> {bill.isPending ? "Saving…" : "Mark billed"}
              </Button>
            </div>
          </div>
        )}

        <LineTable rows={(lines ?? []).map((l) => ({ part: l.part_number, description: l.description, qty: l.qty, cost: l.unit_cost, amount: Number(l.qty || 0) * Number(l.unit_cost || 0), received: l.qty_received }))} total={total} showReceived={po.status !== "draft"} />
      </div>
    </SlideOver>
  );
}

function LineTable({ rows, total, showReceived }: { rows: { part: string | null; description: string | null; qty: number | null; cost: number | null; amount: number | null; dispatch?: string | null; received?: number | null }[]; total: number; showReceived?: boolean }) {
  const hasDispatch = rows.some((r) => r.dispatch);
  const span = (hasDispatch ? 5 : 4) + (showReceived ? 1 : 0);
  return (
    <div className="overflow-hidden rounded-lg border border-hairline">
      {/* COPY-01: the whole part list in one go — "qty × part" per line, the
          shape a vendor's order desk or portal wants. */}
      {rows.some((r) => r.part) && (
        <div className="flex items-center justify-end border-b border-hairline bg-white px-2 py-1">
          <CopyListButton
            items={rows.filter((r) => r.part).map((r) => `${r.qty ?? 1} × ${r.part}${r.description ? ` — ${r.description}` : ""}`)}
            noun="lines"
          />
        </div>
      )}
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
            <th className="px-2 py-1.5">Part</th><th className="px-2 py-1.5">Description</th>
            {hasDispatch && <th className="px-2 py-1.5">Dispatch</th>}
            <th className="px-2 py-1.5 text-right">Qty</th>
            {showReceived && <th className="px-2 py-1.5 text-right">Rec'd</th>}
            <th className="px-2 py-1.5 text-right">Cost</th><th className="px-2 py-1.5 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l, i) => (
            <tr key={i} className="border-t border-hairline">
              <td className="px-2 py-1.5 font-medium text-slate-700">
                {l.part ? <CopyCode value={l.part} label="Copy part number" /> : <span className="text-slate-300">—</span>}
              </td>
              <td className="max-w-[200px] truncate px-2 py-1.5 text-slate-500">{l.description}</td>
              {hasDispatch && (
                <td className="px-2 py-1.5">
                  {l.dispatch
                    ? <Link to={`/dispatch/${l.dispatch}`} className="rounded bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700 hover:bg-brand-100">#{l.dispatch}</Link>
                    : <span className="text-slate-300">—</span>}
                </td>
              )}
              <td className="px-2 py-1.5 text-right tabular-nums">{l.qty}</td>
              {showReceived && (
                <td className={cn("px-2 py-1.5 text-right tabular-nums", Number(l.received ?? 0) >= Number(l.qty ?? 0) && Number(l.qty ?? 0) > 0 ? "text-emerald-700" : Number(l.received ?? 0) > 0 ? "text-amber-700" : "text-slate-300")}>
                  {Number(l.received ?? 0)}
                </td>
              )}
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{money(l.cost)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{money(l.amount)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={span + 1} className="px-2 py-3 text-center text-slate-400">No lines.</td></tr>}
        </tbody>
        {rows.length > 0 && (
          <tfoot><tr className="border-t border-hairline bg-slate-50"><td colSpan={span} className="px-2 py-1.5 text-right font-semibold text-slate-600">Total</td><td className="px-2 py-1.5 text-right font-semibold tabular-nums text-slate-900">{money(total)}</td></tr></tfoot>
        )}
      </table>
    </div>
  );
}

// Links a PO line to the ITEM CATALOG: type a part # → pick a real item →
// auto-fills the description + our cost. So ordering through a PO references the
// catalog, not free text (Seth: no dead links — keep it connected).
function PoLinePartInput({ value, onText, onPick, inputRef, onKeyDown }: { value: string; onText: (t: string) => void; onPick: (it: Item) => void; inputRef?: (el: HTMLInputElement | null) => void; onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void }) {
  const [open, setOpen] = useState(false);
  // PRICEBOOK-PICK-01: the one pricebook search (best first, 12 here) + the full window one click away.
  const { data: matches } = usePricebookSearch(value, { limit: 12 });
  const [browse, setBrowse] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const shown = value.trim().length >= 2 ? (matches ?? []) : [];
  const listOpen = open && shown.length > 0;
  // KEYS-01: ↓ ↑ Enter pick a part; Tab picks the highlighted one and moves on.
  const nav = useListNav({
    count: shown.length,
    open: listOpen,
    typed: value.trim().length >= 2,
    onPick: (i) => { const it = shown[i]; if (it) { onPick(it); setOpen(false); } },
    onClose: () => setOpen(false),
  });
  useEffect(() => { if (listOpen) scrollActiveIntoView(listRef.current, nav.active); }, [listOpen, nav.active]);
  return (
    <div className="relative">
      <input value={value} ref={inputRef}
        role="combobox" aria-expanded={listOpen} aria-autocomplete="list"
        onChange={(e) => { onText(e.target.value); setOpen(true); }}
        onFocus={(e) => { setOpen(true); e.target.select(); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => { nav.onKeyDown(e); if (!e.defaultPrevented) onKeyDown?.(e); }}
        className="poinp w-full" placeholder="search part #" autoComplete="off" />
      {listOpen && (
        <div data-glass="panel" className="absolute z-30 mt-1 w-96 overflow-hidden rounded-xl">
          <div ref={listRef} role="listbox" className="max-h-56 overflow-y-auto py-1">
            {shown.map((it, i) => {
              const cost = itemCostOf(it);
              return (
                <button key={it.id} type="button" role="option" aria-selected={nav.active === i} data-index={i} tabIndex={-1}
                  onMouseDown={() => { onPick(it); setOpen(false); }}
                  onMouseEnter={() => nav.setActive(i)}
                  className={cn("flex w-full items-baseline gap-2 px-2 py-1 text-left text-xs", nav.active === i ? "bg-brand-50" : "hover:bg-slate-50")}>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium text-slate-700">{it.part_number}</span>
                    <span className="ml-1 text-slate-500">{it.description}</span>
                  </span>
                  {cost != null && <span className="shrink-0 tabular-nums text-slate-500">cost {pbMoney(cost)}</span>}
                </button>
              );
            })}
          </div>
          <button type="button" tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); setBrowse(value); setOpen(false); }}
            className="flex w-full items-center justify-between border-t border-slate-100 px-2 py-1.5 text-left text-xs font-semibold text-brand-700 hover:bg-brand-50">
            See all results and details <BookOpen className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <PricebookBrowser open={browse != null} initialQuery={browse ?? ""} multi={false} pickLabel="Use on This Line"
        onClose={() => setBrowse(null)} onPick={(it) => onPick(it)} />
    </div>
  );
}

function PoFormModal({ editPo, prefill, onClose }: { editPo?: NativePo | null; prefill?: PoPrefill | null; onClose: () => void }) {
  const isEdit = !!editPo;
  const { data: vendorRows } = useVendorList("", true);
  const create = useCreateNativePo();
  const update = useUpdateNativePo();
  const { data: existingLines } = useNativePoLines(editPo?.id ?? null);
  const [vendor, setVendor] = useState<string | null>(editPo?.vendor_code ?? null);
  const [terms, setTerms] = useState(editPo?.terms ?? "");
  const [shipVia, setShipVia] = useState(editPo?.ship_via ?? "");
  const [dateReq, setDateReq] = useState(editPo?.date_requested ?? new Date().toISOString().slice(0, 10));
  // PO-03: how it was placed, the vendor's confirmation, when it's due.
  const [method, setMethod] = useState<PoOrderMethod | "">(editPo?.order_method ?? "");
  const [vendorRef, setVendorRef] = useState(editPo?.vendor_order_ref ?? "");
  const [expected, setExpected] = useState(editPo?.expected_date ?? "");
  // Ship to: the office unless somebody deliberately changes it (no drop-ship
  // guesswork — Seth 2026-09-03).
  const [shipTo, setShipTo] = useState(editPo?.ship_to_addr ?? SEDGWICK_SHIP_TO);
  const [editShipTo, setEditShipTo] = useState(false);
  // TICKET-EQUIP-01: the unit this part is for — from the ticket, or picked
  // from the equipment on file at the address.
  const [equip, setEquip] = useState<PartEquipment | null>(editPo?.equipment ?? prefill?.equip ?? null);
  // PARTS-EQUIP-01: the part ticket this PO is for — the unit picked here lands
  // on the ticket too, so the return visit (and the tech's phone) know it.
  const ticketId = editPo?.ticket_id ?? prefill?.ticket ?? null;
  const [addingUnit, setAddingUnit] = useState(false);
  const [newUnit, setNewUnit] = useState({ category: "", brand: "", model: "", serial: "", equip_loc: "" });
  const addUnit = useAddUnit();
  const [memo, setMemo] = useState(
    editPo?.memo ?? (prefill?.equip ? `For ${equipmentLabel(prefill.equip)}${prefill.equip.serial ? ` · S/N ${prefill.equip.serial}` : ""}` : ""),
  );
  // DISPATCH-NO-02: a part ticket hands us the origin call's INTERNAL ref —
  // resolve it to the real sequential call number for the field; the ref
  // still rides along invisibly so the ticket flips to "On order".
  const rawDisp = prefill?.dispatch ?? "";
  const isRef = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawDisp.trim());
  const [dispatchNo, setDispatchNo] = useState(editPo?.dispatch_no ?? (isRef ? "" : rawDisp));
  const dispLookup = useQuery({
    queryKey: ["po_disp_resolve", rawDisp],
    enabled: !isEdit && isRef,
    queryFn: async (): Promise<number | null> => {
      const { data } = await supabase.from("pegasus_dispatch").select("dispatch_no").eq("id", rawDisp.trim()).maybeSingle();
      return (data as { dispatch_no: number | null } | null)?.dispatch_no ?? null;
    },
  });
  useEffect(() => {
    if (dispLookup.data != null) setDispatchNo(String(dispLookup.data));
  }, [dispLookup.data]);
  // The number the vendor will get — shown BEFORE saving (Seth 2026-09-01).
  const nextPo = useQuery({
    queryKey: ["next_po_number"],
    enabled: !isEdit,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<number | null> => {
      const { data, error } = await supabase.rpc("next_po_number");
      if (error) return null;
      return (data as number | null) ?? null;
    },
  });
  const [lines, setLines] = useState<NativePoLineInput[]>(
    prefill && (prefill.part || prefill.desc)
      ? [{ part_number: prefill.part || "", description: prefill.desc || "", qty: 1, unit_cost: 0 }]
      : [{ part_number: "", description: "", qty: 1, unit_cost: 0 }],
  );
  const [buyer, setBuyer] = useState(editPo?.buyer ?? "");
  const [attach, setAttach] = useState<CustomerLite | null>(
    editPo?.custno
      ? { custno: editPo.custno, locno: editPo.locno ?? "", name: `Customer ${editPo.custno}`, address: null }
      : prefill?.custno
        ? { custno: prefill.custno, locno: prefill.locno || "", name: `Customer ${prefill.custno}`, address: null }
        : null,
  );
  const [jobId, setJobId] = useState<string | null>(editPo?.job_id ?? null);
  const { data: custJobs } = useCustomerJobs(attach?.custno ?? null);
  const { data: onFile } = useLocationEquipment(equip ? null : attach?.custno ?? null, attach?.locno || null);
  // PO-02: the PO number IS the deliverable — after create, show it big.
  const [createdPo, setCreatedPo] = useState<NativePo | null>(null);
  const [copied, setCopied] = useState(false);
  const buyerName = useBuyerName(buyer || null).data;
  // Label → input links for the fields whose (i) sits outside their <label>.
  const expectedId = useId();
  const dispatchId = useId();

  // Create: autofill ordered-by from the signed-in user. Edit: keep the PO's original buyer.
  useEffect(() => { if (!isEdit) supabase.auth.getUser().then(({ data }) => setBuyer(data?.user?.email ?? "")); }, [isEdit]);
  // "For job" shows the NAME + ADDRESS, never "Customer 0016653" (Seth
  // 2026-08-31) — hydrate the placeholder chip from the account.
  useEffect(() => {
    const a = attach;
    if (!a || !a.name.startsWith("Customer ") || !a.custno) return;
    let alive = true;
    void (async () => {
      let q = supabase.from("mirror_location").select("custno, locno, locname, add1, city").eq("custno", a.custno).order("locno").limit(1);
      if (a.locno) q = supabase.from("mirror_location").select("custno, locno, locname, add1, city").eq("custno", a.custno).eq("locno", a.locno).limit(1);
      const { data } = await q.maybeSingle();
      const l = data as { locno: string; locname: string | null; add1: string | null; city: string | null } | null;
      if (alive && l) {
        setAttach({ custno: a.custno, locno: l.locno ?? a.locno, name: l.locname ?? a.name, address: `${l.add1 ?? ""}${l.city ? ", " + l.city : ""}` });
      }
    })();
    return () => { alive = false; };
  }, [attach]);
  // Edit: hydrate the form with the PO's existing lines (fall back to one blank row).
  useEffect(() => {
    if (isEdit && existingLines) {
      setLines(existingLines.length
        ? existingLines.map((l) => ({ part_number: l.part_number, description: l.description, qty: l.qty, unit_cost: l.unit_cost }))
        : [{ part_number: "", description: "", qty: 1, unit_cost: 0 }]);
    }
  }, [isEdit, existingLines]);

  const vendorOptions = useMemo(
    () => (vendorRows ?? []).map((v) => ({ value: v.vendor_code, label: v.name ?? v.vendor_code, hint: v.name ? `· ${v.vendor_code}` : undefined })),
    [vendorRows],
  );
  const onVendor = (code: string | null) => {
    setVendor(code);
    const vr = (vendorRows ?? []).find((v) => v.vendor_code === code);
    if (vr?.terms) setTerms(vr.terms); // smart: pull the vendor's default terms
  };

  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);
  const setLine = (i: number, patch: Partial<NativePoLineInput>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i: number) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  const busy = create.isPending || update.isPending;
  const canSave = !!vendor && lines.some((l) => l.part_number || l.description) && !busy;
  // KEYS-01: Enter = next field, Ctrl+Enter = Save; a new line opens itself
  // when you Tab (or Enter) off the last line's cost with something on it.
  const partRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusLine, setFocusLine] = useState<number | null>(null);
  useEffect(() => {
    if (focusLine == null) return;
    const el = partRefs.current[focusLine];
    if (el) { el.focus(); setFocusLine(null); }
  }, [focusLine, lines.length]);
  const addLineAndFocus = () => { setLines((ls) => { setFocusLine(ls.length); return [...ls, { part_number: "", description: "", qty: 1, unit_cost: 0 }]; }); };

  const onFormKeys = useEntryFormKeys(() => { if (canSave) submit("save"); });

  const submit = (mode: "save" | "receive" | "draft") => {
    const header = {
      vendor_code: vendor, terms: terms || null, ship_via: shipVia || null,
      ship_to_addr: shipTo.trim() || SEDGWICK_SHIP_TO, date_requested: dateReq,
      expected_date: expected || null, order_method: method || null, vendor_order_ref: vendorRef || null,
      custno: attach?.custno ?? null, locno: attach?.locno ?? null, job_id: jobId, memo,
      dispatch_no: dispatchNo || null,
      ticket_id: ticketId, equipment: equip,
    };
    if (isEdit && editPo) {
      update.mutate({ id: editPo.id, ...header, lines }, { onSuccess: onClose });
    } else {
      create.mutate({
        ...header,
        origin_ref: isRef ? rawDisp.trim() : null,
        lines, receive: mode === "receive", draft: mode === "draft",
      }, { onSuccess: (po) => setCreatedPo(po) });
    }
  };

  // The number the vendor gets — front and center the moment it exists.
  if (createdPo) {
    const num = createdPo.po_number ?? "";
    return (
      <Modal open onClose={onClose} title="Purchase Order Created" size="md">
        <div className="py-2 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Give the vendor this number</p>
          {/* COPY-01: the number itself is the copy target — tap it, click it, hold it. */}
          <div className="mt-1 text-5xl font-extrabold tabular-nums tracking-tight text-slate-900">
            <CopyCode value={`PO ${num}`} label="Copy PO number" variant="text" size="lg" mono={false} className="text-5xl font-extrabold">PO {num}</CopyCode>
          </div>
          <button type="button"
            onClick={() => { void copyText(`PO ${num}`).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 2500); }); }}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy PO number"}
          </button>
          <p className="mx-auto mt-3 max-w-sm text-xs leading-relaxed text-slate-500">
            {createdPo.status === "draft"
              ? "Saved as a draft — nothing is placed with the vendor yet. Open it and choose Place with vendor when you call it in. "
              : createdPo.status === "received"
                ? "Saved and received — the parts are in stock and any waiting ticket shows Arrived. "
                : dispatchNo ? "The part ticket flipped to On order with this PO number attached — Today and the board show it. " : ""}
            It&apos;s at the top of the Purchase Orders page whenever you need it again.
          </p>
          <Button size="sm" className="mt-4" onClick={onClose}><Check className="h-3.5 w-3.5" /> Done</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose}
      title={isEdit ? `Edit PO ${editPo!.po_number ?? ""}` : nextPo.data ? `New purchase order — will be PO ${nextPo.data}` : "New purchase order"}
      subtitle={isEdit ? "Draft PO" : nextPo.data
        ? <>Give the vendor <strong className="text-slate-700">PO {nextPo.data}</strong> — it locks in the moment you save.</>
        : "PO number assigned on save"} size="lg">
      <div className="space-y-4" data-entry-form onKeyDown={onFormKeys}>
        <div className="grid grid-cols-2 gap-3">
          <PoField label="Vendor">
            <ComboBox value={vendor} onChange={onVendor} options={vendorOptions} placeholder="Pick a vendor…" allowAdd={false} autoFocus />
          </PoField>
          <PoField label="Ordered by">
            <input readOnly tabIndex={-1} value={buyerName ?? buyer} className="poinp w-full bg-slate-50 text-slate-500" />
          </PoField>
          <PoField label="Terms">
            <select value={terms} onChange={(e) => setTerms(e.target.value)} className="poinp w-full">
              <option value="">—</option>
              {PO_TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </PoField>
          <PoField label="Ship via">
            <select value={shipVia} onChange={(e) => setShipVia(e.target.value)} className="poinp w-full">
              <option value="">—</option>
              {PO_SHIP_VIA.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </PoField>
          <PoField label="Date requested">
            <input type="date" value={dateReq} onChange={(e) => setDateReq(e.target.value)} className="poinp w-full" />
          </PoField>
          <PoField label="Expected (optional)" htmlFor={expectedId}
            info="It shows as the ETA on the part ticket." infoLabel="Where the expected date shows">
            <input id={expectedId} type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className="poinp w-full" />
          </PoField>
          <PoField label="How it's being placed">
            <select value={method} onChange={(e) => setMethod(e.target.value as PoOrderMethod | "")} className="poinp w-full">
              <option value="">—</option>
              {PO_ORDER_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </PoField>
          <PoField label="Vendor order # (optional)">
            <input type="text" value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} placeholder="their confirmation number" className="poinp w-full" />
          </PoField>
          <PoField label="Dispatch # (optional)" htmlFor={dispatchId}
            info="With a dispatch #, the part ticket flips to “On order” automatically when this PO saves." infoLabel="What the dispatch # does">
            <input
              id={dispatchId}
              type="text" value={dispatchNo} onChange={(e) => setDispatchNo(e.target.value)}
              placeholder="ties the PO to the service call"
              className="poinp w-full"
            />
            {isRef && dispLookup.isLoading && <p className="mt-1 text-[11px] text-slate-400">Looking up the call number…</p>}
          </PoField>
          <PoField label="For job (optional)">
            <PoCustomerPicker attach={attach} onPick={(c) => { setAttach(c); setJobId(null); }} onClear={() => { setAttach(null); setJobId(null); }} />
            {attach && (custJobs && custJobs.length > 0 ? (
              <select value={jobId ?? ""} onChange={(e) => setJobId(e.target.value || null)} className="poinp mt-1.5 w-full">
                <option value="">— attach to a specific job (optional) —</option>
                {custJobs.map((j) => <option key={j.id} value={j.id}>{j.title ?? "Install"} · {j.status}</option>)}
              </select>
            ) : (
              <p className="mt-1 text-[11px] text-slate-400">No Pegasus jobs for this customer yet — the part attaches to the address.</p>
            ))}
          </PoField>
        </div>

        {/* TICKET-EQUIP-01 / PARTS-EQUIP-01: which unit is this part for? The
            unit the tech marked in the field comes in on the ticket; otherwise
            pick from what's on file at the address — or add a unit that isn't
            on file yet (Seth 2026-09-10). */}
        {(equip || attach) && (
          <div>
            <div className="mb-1 flex items-center gap-2 text-[11px] font-medium text-slate-500">
              <span>{equip ? (equip.src === "peg" && !equip.install_date ? "Ordering for this unit (added to the record)" : "Ordering for this unit") : (onFile?.length ?? 0) > 0 ? "Equipment on file at this address — which unit is it for?" : "No equipment on file at this address"}</span>
              {!equip && !addingUnit && (
                <button type="button" onClick={() => setAddingUnit(true)} className="ml-auto inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-semibold text-brand-700 hover:bg-brand-50"><Plus className="h-3 w-3" /> Add a unit</button>
              )}
            </div>
            {equip ? (
              <EquipmentCard e={equip} onClear={() => { const auto = `For ${equipmentLabel(equip)}${equip.serial ? ` · S/N ${equip.serial}` : ""}`; if (memo.trim() === auto) setMemo(""); setEquip(null); }} />
            ) : addingUnit ? (
              <div className="rounded-lg border border-brand-200 bg-brand-50/30 p-2.5">
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  <select value={newUnit.category} onChange={(e) => setNewUnit({ ...newUnit, category: e.target.value })} className="poinp">
                    <option value="">— type —</option>
                    {["Furnace", "A/C", "Heat Pump", "Boiler", "Air Handler", "Mini-Split", "Water Heater", "Humidifier", "Air Cleaner", "Thermostat", "Other"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input value={newUnit.brand} onChange={(e) => setNewUnit({ ...newUnit, brand: e.target.value })} className="poinp" placeholder="brand (Lennox, Carrier…)" />
                  <input value={newUnit.model} onChange={(e) => setNewUnit({ ...newUnit, model: e.target.value })} className="poinp" placeholder="model number" />
                  <input value={newUnit.serial} onChange={(e) => setNewUnit({ ...newUnit, serial: e.target.value })} className="poinp font-mono" placeholder="serial number" />
                  <input value={newUnit.equip_loc} onChange={(e) => setNewUnit({ ...newUnit, equip_loc: e.target.value })} className="poinp" placeholder="where (basement, attic, …)" />
                  <div className="flex items-center gap-1.5">
                    <button type="button" disabled={addUnit.isPending || !attach?.custno || !(newUnit.model.trim() || newUnit.serial.trim() || newUnit.brand.trim())}
                      onClick={() => {
                        if (!attach?.custno) return;
                        addUnit.mutate({ custno: attach.custno, locno: attach.locno || null, category: newUnit.category || null, brand: newUnit.brand.trim() || null, model: newUnit.model.trim() || null, serial: newUnit.serial.trim() || null, equip_loc: newUnit.equip_loc.trim() || null, origin_dispatch: isRef ? rawDisp.trim() : (dispatchNo || null) },
                          { onSuccess: (u) => { setEquip(u); setAddingUnit(false); setNewUnit({ category: "", brand: "", model: "", serial: "", equip_loc: "" }); if (!memo.trim()) setMemo(`For ${equipmentLabel(u)}${u.serial ? ` · S/N ${u.serial}` : ""}`); } });
                      }}
                      className="rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">
                      {addUnit.isPending ? "Saving…" : "Save the unit"}
                    </button>
                    <button type="button" onClick={() => setAddingUnit(false)} className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100">Cancel</button>
                    <InfoTip label="Where the new unit goes">It goes on the customer&apos;s equipment record right away. The tech sees it on the phone, and the return visit knows which unit the part is for.</InfoTip>
                  </div>
                </div>
                {addUnit.isError && <p className="mt-1 text-[11px] font-semibold text-rose-600">Couldn't save the unit — try again.</p>}
              </div>
            ) : (onFile?.length ?? 0) > 0 ? (
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {(onFile ?? []).slice(0, 6).map((u, i) => (
                  <button key={`${u.counter ?? i}`} type="button"
                    onClick={() => { setEquip(u); if (!memo.trim()) setMemo(`For ${equipmentLabel(u)}${u.serial ? ` · S/N ${u.serial}` : ""}`); }}
                    className="rounded-lg border border-hairline bg-white px-2.5 py-1.5 text-left text-xs transition-colors hover:border-brand-300 hover:bg-brand-50/40">
                    <div className="truncate font-semibold text-slate-800">{equipmentLabel(u)}{u.src === "peg" && <span className="ml-1.5 rounded bg-slate-100 px-1 py-px text-[9px] font-bold text-slate-500">field-added</span>}</div>
                    <div className="truncate text-[11px] text-slate-500">{[u.eq_type, u.serial ? `S/N ${u.serial}` : null, installYear(u.install_date) ? `installed ${installYear(u.install_date)}` : null].filter(Boolean).join(" · ")}</div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}

        <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          <div className="flex items-start gap-2">
            <span className="shrink-0 font-medium text-slate-600">Ship to:</span>
            {editShipTo ? (
              <div className="min-w-0 flex-1">
                <textarea rows={3} value={shipTo} onChange={(e) => setShipTo(e.target.value)} className="poinp w-full resize-none" />
                <div className="mt-1 flex gap-3">
                  <button type="button" onClick={() => setEditShipTo(false)} className="font-medium text-brand-700 hover:underline">Done</button>
                  <button type="button" onClick={() => { setShipTo(SEDGWICK_SHIP_TO); setEditShipTo(false); }} className="text-slate-400 hover:text-slate-600">Use the office address</button>
                </div>
              </div>
            ) : (
              <span className="min-w-0 flex-1">
                {shipTo.split("\n").filter(Boolean).join(" · ")}
                {shipTo.trim() !== SEDGWICK_SHIP_TO && <span className="ml-1 rounded bg-amber-50 px-1 py-px text-[10px] font-semibold text-amber-700">not the office</span>}
                <button type="button" onClick={() => setEditShipTo(true)} className="ml-2 font-medium text-brand-700 hover:underline">Change</button>
              </span>
            )}
          </div>
        </div>

        <div>
          <div className="mb-1 grid grid-cols-[1fr_1.4fr_auto_auto_auto] gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <span>Part #</span><span>Description</span><span className="w-14 text-right">Qty</span><span className="w-20 text-right">Cost</span><span className="w-6" />
          </div>
          <div className="space-y-1.5">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_1.4fr_auto_auto_auto] items-center gap-2">
                <PoLinePartInput value={l.part_number ?? ""}
                  inputRef={(el) => { partRefs.current[i] = el; }}
                  onText={(t) => setLine(i, { part_number: t })}
                  onPick={(it) => setLine(i, { part_number: it.part_number, description: it.description ?? "", unit_cost: it.last_purchase ?? it.average_cost ?? l.unit_cost })} />
                <input value={l.description ?? ""} onChange={(e) => setLine(i, { description: e.target.value })} onFocus={(e) => e.target.select()} className="poinp" placeholder="description" />
                <input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} onFocus={(e) => e.target.select()} className="poinp w-14 text-right" />
                <input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, { unit_cost: Number(e.target.value) })} onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => {
                    // Off the last line's cost with something on the line → a fresh line, cursor in its part #.
                    if ((e.key === "Tab" && !e.shiftKey) || (e.key === "Enter" && !e.ctrlKey && !e.metaKey)) {
                      if (i === lines.length - 1 && (l.part_number || l.description)) { e.preventDefault(); addLineAndFocus(); }
                    }
                  }}
                  className="poinp w-20 text-right" />
                <button type="button" tabIndex={-1} onClick={() => removeLine(i)} title="Remove this line" className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <button type="button" tabIndex={-1} onClick={addLineAndFocus} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"><Plus className="h-3.5 w-3.5" /> Add line</button>
        </div>

        <PoField label="Memo">
          <textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} className="poinp resize-none w-full" />
        </PoField>

        {/* PO-03: ESC's Save / Save & Receive shape. Save = the order is placed
            with the vendor (the number is what you gave them). Draft is the
            exception — you haven't called it in yet. */}
        <div className="flex items-center justify-between border-t border-hairline pt-3">
          <span className="text-sm text-slate-500">Total <strong className="ml-1 tabular-nums text-slate-900">{money(total)}</strong></span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            {!isEdit && (
              <>
                <button type="button" disabled={!canSave} onClick={() => submit("draft")} title="Keep it as a draft — not placed with the vendor yet" className="px-1 text-xs font-medium text-slate-500 hover:text-slate-800 disabled:opacity-50">
                  Save as draft
                </button>
                <Button variant="secondary" size="sm" disabled={!canSave} onClick={() => submit("receive")} title="The parts are in hand right now (counter pickup) — saves the PO and receives it into stock in one step">
                  <Check className="h-3.5 w-3.5" /> Save &amp; Receive
                </Button>
              </>
            )}
            <Button size="sm" disabled={!canSave} onClick={() => submit("save")} title={isEdit ? "Ctrl+Enter" : "Placed with the vendor — the PO number is theirs now · Ctrl+Enter"}>
              <Check className="h-3.5 w-3.5" /> {busy ? "Saving…" : isEdit ? "Save changes" : "Save"}
            </Button>
          </div>
        </div>
      </div>
      <style>{`.poinp{border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.35rem 0.5rem;font-size:0.8125rem}.poinp:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 2px rgb(99 102 241 / 0.25)}`}</style>
    </Modal>
  );
}

function PoField({ label, info, infoLabel, htmlFor, children }: {
  label: string;
  /** DESC-CLEAN-01: an explanation for the (i) beside the label. */
  info?: ReactNode;
  infoLabel?: string;
  /** The input's id — needed with `info`, since the label no longer wraps it. */
  htmlFor?: string;
  children: ReactNode;
}) {
  if (info) {
    // A button inside a <label> would take the label's click, so the (i) sits
    // beside it. -my-1 keeps this label row as tall as its plain neighbors'.
    return (
      <div className="text-sm">
        <div className="mb-1 flex items-center gap-1.5">
          <label htmlFor={htmlFor} className="text-[11px] font-medium text-slate-500">{label}</label>
          <InfoTip label={infoLabel ?? `About ${label}`} className="-my-1">{info}</InfoTip>
        </div>
        {children}
      </div>
    );
  }
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-[11px] font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

// Attach a PO to a job — search the customer/address (feeds invoicing later).
function PoCustomerPicker({ attach, onPick, onClear }: { attach: CustomerLite | null; onPick: (c: CustomerLite) => void; onClear: () => void }) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const { data: matches } = useCustomerLookup(term);
  if (attach) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-brand-200 bg-brand-50/50 px-2 py-1.5 text-xs">
        <button type="button"
          onClick={() => window.open(`/customers/${attach.custno}${attach.locno ? `?loc=${attach.locno}` : ""}`, "_blank", "noopener")}
          title="Open this customer in a new tab (your PO stays put)"
          className="min-w-0 truncate text-left font-medium text-brand-700 hover:underline">
          {attach.name}{attach.address ? <span className="font-normal text-slate-500"> · {attach.address}</span> : null}
        </button>
        <button type="button" onClick={onClear} title="Detach from this customer" className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  const shown = open && term.trim().length >= 2 ? (matches ?? []) : [];
  const nav = useListNav({
    count: shown.length,
    open: shown.length > 0,
    typed: term.trim().length >= 2,
    onPick: (i) => { const c = shown[i]; if (c) { onPick(c); setOpen(false); } },
    onClose: () => setOpen(false),
  });
  return (
    <div className="relative">
      <input value={term} onChange={(e) => { setTerm(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        role="combobox" aria-expanded={shown.length > 0} aria-autocomplete="list" autoComplete="off"
        onKeyDown={nav.onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)} className="poinp w-full" placeholder="search name or address" />
      {shown.length > 0 && (
        <div role="listbox" data-glass="panel" className="absolute z-30 mt-1 max-h-48 w-full overflow-y-auto rounded-xl py-1">
          {shown.map((c, i) => (
            <button key={`${c.custno}-${c.locno}`} type="button" role="option" aria-selected={nav.active === i} data-index={i} tabIndex={-1}
              onMouseDown={() => { onPick(c); setOpen(false); }}
              onMouseEnter={() => nav.setActive(i)}
              className={cn("block w-full truncate px-2 py-1 text-left text-xs", nav.active === i ? "bg-brand-50" : "hover:bg-slate-50")}>
              <span className="font-medium text-slate-700">{c.name}</span> <span className="text-slate-400">{c.address}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
